import { useState } from 'react';
import { Helmet } from 'react-helmet';
import {
  Grid,
  Button,
  Box,
  Typography,
  useMediaQuery,
  Slider,
} from '@mui/material';
import { useAccount } from '../../contexts/Account';
import {
  submitAOBFStep3,
  updateAdvisorOnboardingStep6,
} from '../../services/advisor_services';
import LoadingLogoSkeleton from '../../components/SkeletonLoading/loadingLogoSkeleton';
import ACSnackbar from '../../components/ACSnackbar';
import AdvisoryCloudLogo from '../../components/AdvisoryCloudLogo';
import RatingCard from './RatingCard';
import { AdvisorInterests } from '../../services/utils/types';
import CustomInterestsModal from '../../components/Advisor/CustomInterestsModal';

const useStyles = (isSM) => ({
  main: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  header: {
    display: 'flex',
    padding: isSM ? '16px 8px' : '16px 120px 0',
    justifyContent: 'center',
    width: '100%',
  },
  body: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    maxWidth: '600px',
  },
  title: {
    fontSize: '28px',
    fontWeight: 700,
    lineHeight: { xxs: '32px', xs: '24px' },
    letterSpacing: '-0.4300000071525574px',
    marginBottom: '16px',
    padding: { xxs: '0px 8px', xs: '0px' },
    textAlign: 'center',
  },
  subtitle: {
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '-0.4300000071525574px',
    color: '#646D7A',
    maxWidth: '360px',
    textAlign: 'center',
  },
  info: {
    fontStyle: 'italic',
    fontSize: '14px',
    fontWeight: 300,
    lineHeight: '24px',
    letterSpacing: '-0.4300000071525574px',
    color: '#646D7A',
    marginBottom: '16px',
  },
  gridContainerProps: {
    container: true,
    spacing: 0,
    justifyContent: 'center',
  },
  gridItemProps: {
    item: true,
    xxs: 12,
    md: 8,
  },
  gridCentered: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  tagButton: {
    textTransform: 'none',
    fontWeight: 500,
    fontSize: '12px',
    borderRadius: '16px',
    margin: { xxs: '8px 0px', xs: '8px 0px' },
    padding: '6px 12px',
    width: '100%',
  },
  salaryDisplay: {
    fontSize: '36px',
    fontWeight: 800,
    textAlign: 'center',
    mt: 1,
    mb: 1,
  },
  sliderBox: {
    width: '100%',
    maxWidth: 560,
    px: { xxs: 3, xs: 2 },
  },
});

const currency = (n, addPlusAtMax = false) => {
  if (!Number.isFinite(n)) return '$0';
  const isMax = n > 500000 && addPlusAtMax;
  const formatted = (isMax ? 500000 : n).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
  return isMax ? `${formatted}+` : formatted;
};

function AOBFStep3() {
  const { accountDetails, currentPlan } = useAccount();
  const isSM = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const classes = useStyles(isSM);

  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [salary, setSalary] = useState(300000);

  const [interestsSelected, setInterestsSelected] = useState(
    accountDetails.interests || []
  );
  const [customInterests, setCustomInterests] = useState([]);
  const [customInterestModalOpen, setCustomInterestModalOpen] = useState(false);
  const [errorSnackbar, setErrorSnackbar] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    try {
      setIsLoading(true);
      await submitAOBFStep3({
        id: accountDetails.id,
        interests: interestsSelected,
        annualSalary: Number(salary) > 500000 ? 500000 : (Number(salary) || 0),
      });
      await updateAdvisorOnboardingStep6({
        ADVISOR_ID: accountDetails.id,
        ONBOARDING_STATUS: 'Complete',
      });
      window.location.replace(currentPlan.onboardingRedirectUrl);
    } catch (error) {
      setIsLoading(false);
      setOpenSnackbar(true);
      console.error(error);
    }
  };

  const disableButton = () => isLoading || interestsSelected.length === 0;

  const marks = [
    { value: 100000, label: '$100,000' },
    { value: 300000, label: '$300,000' },
    { value: 500000, label: '$500,000+' },
  ];

  return (
    <>
      <Helmet>
        <title>Profile Builder - AdvisoryCloud</title>
      </Helmet>

      <Box sx={classes.main}>
        <Box sx={classes.header}>
          <AdvisoryCloudLogo
            removeHyperlink
            onClick={() => {}}
            width={isSM ? 240 : 180}
            height={isSM ? 80 : 60}
            cursor="default"
          />
        </Box>

        <Box sx={classes.body}>
          <Typography sx={classes.title}>
            Experience the ROI of AdvisoryCloud
          </Typography>
          <Typography sx={{ ...classes.subtitle, maxWidth: '90%' }}>
            In addition to enjoying the benefits being offered to board members,
            AdvisoryCloud allows you to attract networking and advisory work
            requests that align with your unique interests and expertise.
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ p: 2 }}>
            <Grid {...classes.gridContainerProps}>
              <Grid {...classes.gridItemProps} sx={classes.gridCentered}>
                <Typography sx={classes.label}>
                  Which of the following interest you?
                </Typography>
                <Typography sx={classes.info}>Check all that apply:</Typography>
              </Grid>
              <Grid
                {...classes.gridItemProps}
                mb="10px"
                xs={6}
                sm={6}
                md={6}
                sx={classes.gridCentered}
              >
                {[...AdvisorInterests, ...customInterests].map((tag, index) => (
                  <Button
                    key={index}
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      if (
                        interestsSelected.length >= 15 &&
                        !interestsSelected.includes(tag)
                      ) {
                        setErrorSnackbar(true);
                        return;
                      }
                      if (tag === 'Create My Own...') {
                        setCustomInterestModalOpen(true);
                        return;
                      }
                      setInterestsSelected((prev) =>
                        prev.includes(tag)
                          ? prev.filter((interest) => interest !== tag)
                          : [...prev, tag]
                      );
                    }}
                    sx={{
                      ...classes.tagButton,
                      backgroundColor: interestsSelected.includes(tag)
                        ? '#2B71F6'
                        : '#F5F7FA',
                      color: interestsSelected.includes(tag)
                        ? '#FFFFFF'
                        : '#232B35',
                      borderColor: interestsSelected.includes(tag)
                        ? '#2B71F6'
                        : '#F5F7FA',
                      '&:hover': {
                        backgroundColor: interestsSelected.includes(tag)
                          ? '#2B71F6'
                          : '#F5F7FA',
                        color: interestsSelected.includes(tag)
                          ? '#FFFFFF'
                          : '#232B35',
                        borderColor: interestsSelected.includes(tag)
                          ? '#2B71F6'
                          : '#F5F7FA',
                      },
                    }}
                  >
                    {tag}
                  </Button>
                ))}
              </Grid>

              <Grid {...classes.gridItemProps} sx={{ mt: 1, mb: 2 }}>
                <Typography sx={classes.label}>
                  What is your approximate annual income? (optional)
                </Typography>

                <Typography sx={classes.salaryDisplay}>
                  {currency(salary, true)}
                </Typography>

                <Box sx={classes.sliderBox}>
                  <Slider
                    id="aobf-salary-slider"
                    value={salary}
                    min={50000}
                    max={550000}
                    step={25000}
                    marks={marks}
                    onChange={(_, v) => setSalary(v)}
                    aria-labelledby="aobf-salary-slider"
                  />
                </Box>
              </Grid>
              <Grid {...classes.gridItemProps}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  fullWidth
                  disabled={disableButton()}
                  id="aobf-submit-button"
                  sx={{
                    borderRadius: '50px',
                    backgroundColor: '#3171F6',
                    '&:hover': { backgroundColor: '#3171F6' },
                    height: '48px',
                    textTransform: 'none',
                    fontSize: '16px',
                    fontWeight: 700,
                    mb: '16px',
                    mt: '8px',
                  }}
                >
                  {isLoading ? <LoadingLogoSkeleton /> : 'Finish'}
                </Button>
              </Grid>
            </Grid>
            <Grid {...classes.gridContainerProps}>
              <Grid {...classes.gridItemProps}>
                <RatingCard
                  quote="I have used many other professional networking services/sites with varying degrees of satisfaction - AdvisoryCloud handily beats all of them."
                  name="Sweetser"
                  title="Senior VP Financial, Planning & Analysis"
                />
              </Grid>
            </Grid>
            <ACSnackbar
              onClose={() => setOpenSnackbar(false)}
              open={openSnackbar}
              severity="error"
              text="Something went wrong"
            />
          </Box>
        </Box>
        <CustomInterestsModal
          open={customInterestModalOpen}
          setOpen={setCustomInterestModalOpen}
          interestsSelected={interestsSelected}
          customInterests={customInterests}
          setCustomInterests={setCustomInterests}
          setInterestsSelected={setInterestsSelected}
          setErrorSnackbar={setErrorSnackbar}
        />
        <ACSnackbar
          onClose={() => setErrorSnackbar(false)}
          open={errorSnackbar}
          severity="error"
          text="You can only select up to 15 interests."
        />
      </Box>
    </>
  );
}

export default AOBFStep3;
